import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@/lib/navigation';
import { TripFormValues } from '@/components/trips/form/TripForm';
import { buildTripRequestPayload, type TripPayloadOverrides } from '@/hooks/trips/useCreateTrip';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface UpdateTripVariables {
  id: string;
  data: TripFormValues;
  overrides?: TripPayloadOverrides;
}

async function updateTrip({ id, data, overrides }: UpdateTripVariables) {
  const payload = buildTripRequestPayload(data, overrides);

  const response = await apiFetch(`/api/v1/business-trip-requests/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update trip' }));
    throw new Error((error as { detail?: string }).detail || 'Failed to update trip');
  }

  return response.json();
}

export function useUpdateTripForm() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: updateTrip,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      router.push(`/trips/${variables.id}`);
    },
    onError: (error: Error) => {
      console.error('Failed to update trip:', error);
    },
  });
}
